import * as React from 'react';
import { useSelector } from 'react-redux';
import Form from 'antd/lib/form';
import Text from 'antd/lib/typography/Text';
import { fromPairs } from 'lodash';
import { AgentCIWorkflowRequest } from '@/actions/orgSettings';
import { getSeparatedWorkflowFields } from '@/helpers/settingFieldHelpers';
import { workflowFieldsFromBucketFormValue } from '@/helpers/integrationHelpers';
import { RootState } from '@/reducers/reducers';
import { selectBuildSystemIntegrations, selectUserAppDataByAppIdMap } from '@/selectors/selectors';
import { Agent, IntegrationType } from '@/types';
import { useIntegrationSettingFields } from '@/utils/hooks/useIntegrationSettingFields';

export interface CIWorkflowFormValue {
    integrationId?: string;
    workflow?: string | string[];
}

export type CIWorkflowsFormValues = Record<string, CIWorkflowFormValue>;

export const initialCIWorkflowFormValues = (agent?: Agent): CIWorkflowsFormValues =>
    fromPairs(
        Object.entries(agent?.ciWorkflows ?? {}).map(([appId, { integrationId, workflow }]) => [
            appId,
            {
                integrationId,
                workflow: workflow.appSlug ? [workflow.appSlug, workflow.workflowId] : workflow.workflowId,
            },
        ]),
    );

export const toCIWorkflowsRequest = (
    values: CIWorkflowsFormValues | undefined,
    appIds: string[],
): Record<string, AgentCIWorkflowRequest> =>
    fromPairs(
        appIds.map((appId) => {
            const { integrationId = '', workflow = '' } = values?.[appId] ?? {};
            const { workflowId, appSlug } = workflowFieldsFromBucketFormValue(workflow);
            return [appId, { integrationId, workflow: { workflowId, appSlug } }];
        }),
    );

const AppWorkflowRow: React.FC<{ orgId: string; appId: string }> = ({ orgId, appId }) => {
    const appData = useSelector((state: RootState) => selectUserAppDataByAppIdMap(state, orgId)[appId]);
    const gitHubIntegration = useSelector((state: RootState) =>
        selectBuildSystemIntegrations(state, appId).find((integration) => integration.id === IntegrationType.GitHubCI),
    );
    const { settingFields } = useIntegrationSettingFields(orgId, appId, gitHubIntegration?.installationId);
    const picker = getSeparatedWorkflowFields(settingFields, true).workingBranchWorkflowFields[0];

    return (
        <>
            <Form.Item
                name={['ciWorkflows', appId, 'integrationId']}
                initialValue={gitHubIntegration?.installationId}
                hidden
            >
                <input />
            </Form.Item>
            <Form.Item
                name={['ciWorkflows', appId, 'workflow']}
                label={appData?.appName ?? appId}
                extra={gitHubIntegration ? undefined : 'Connect GitHub Actions to this app first.'}
                rules={[
                    {
                        validator: (_, value) =>
                            !gitHubIntegration ? Promise.reject('This app has no GitHub Actions integration')
                            : value ? Promise.resolve()
                            : Promise.reject('Select a workflow'),
                    },
                ]}
            >
                {picker?.renderField?.(null, { placeholder: 'Select workflow', disabled: !gitHubIntegration })}
            </Form.Item>
        </>
    );
};

export const AgentCIWorkflowFields: React.FC<{ orgId: string; appIds: string[] }> = ({ orgId, appIds }) => (
    <>
        <Text>Workflow to run for each app</Text>
        <div style={{ marginTop: 8 }}>
            {appIds.map((appId) => (
                <AppWorkflowRow key={appId} orgId={orgId} appId={appId} />
            ))}
        </div>
    </>
);
